import { IdvPreference } from "@amzn/client-workbench-api-model";
import { Button, ButtonVariant } from "@amzn/stencil-react-components/button";
import { Row, Spacer } from "@amzn/stencil-react-components/layout";
import {
  MessageBanner,
  MessageBannerType,
} from "@amzn/stencil-react-components/message-banner";
import { Spinner, SpinnerSize } from "@amzn/stencil-react-components/spinner";
import { Text } from "@amzn/stencil-react-components/text";
import { trackStructEvent } from "@snowplow/browser-tracker";
import React, { useContext, useEffect, useState } from "react";
import { FormattedMessage } from "react-intl";

import ITChatVerificationGuidance from "@/components/IdentityCheck/ITChatVerificationGuidance";
import { useGetIdvPreference } from "@/components/IdentityCheck/IVV/hooks/useGetIdvPreference";
import { useGetWizardEntitlements } from "@/components/IdentityCheck/WizardPathChooser/hooks/useGetWizardEntitlements";
import {
  Card,
  CardClickedCallbackEventBody,
  IdentityVerificationSelectionCard,
} from "@/components/IdentityCheck/WizardPathChooser/IdentityVerificationSelectionCard";
import { useShowChimeIdvGuidance } from "@/hooks/useShowChimeIdvGuidance";
import { AppContext } from "@/stores/appStore";
import { ActionType, WizardPathType } from "@/stores/constants";
import { Day1EventAction, Day1EventCategory } from "@/types/snowplow-events";

export const DATA_TEST_ID_WIZARD_PATH_CHOOSER = "WizardPathChooserView";

const IDV_PREFERENCE_TO_WIZARD_PATH_TYPE: Record<
  IdvPreference,
  WizardPathType | undefined
> = {
  NONE: undefined,
  AUTOMATED: "IVV",
  MANUAL: "ManualRIV",
};

/**
 * Mapping of {@link Card} to the {@link Day1EventAction}. Whenever a card is selected by the
 * new hire, the app should emit a Snowplow metric with the values in this mapping as the
 * metric action.
 */
const CARD_TYPE_TO_SNOWPLOW_ACTION: Readonly<
  Record<Exclude<Card, undefined>, Day1EventAction>
> = {
  ManualRIV: Day1EventAction.IDVSelectManual,
  IVV: Day1EventAction.IDVSelectAutomated,
  ContactSupport: Day1EventAction.IDVSelectContactSupport,
};

export const getTextForIdsRequiredField = (
  requiredDocsWithGesturesCount: number,
  verificationType: WizardPathType | "ContactSupport"
): string => {
  if (requiredDocsWithGesturesCount <= 2) {
    return "identityCheck-pathchooser-card-oneId";
  }
  if (verificationType === "IVV") {
    return "identityCheck-pathchooser-card-twoIdsAutomated";
  }
  return "identityCheck-pathchooser-card-twoIdsManual";
};

export const WizardPathChooser = (): JSX.Element => {
  const { dispatch, state } = useContext(AppContext);
  const [selectedCard, setSelectedCard] = useState<Card | undefined>(undefined);
  const [selectionRequiredError, setSelectionRequiredError] = useState(false);
  const {
    entitlements,
    loading: getWizardEntitlementsLoading,
    error: getWizardEntitlementsError,
    metadata,
  } = useGetWizardEntitlements();

  const {
    isLoading: idvPreferenceLoading,
    data: idvPreferenceFromApi,
    isError: idvPreferenceError,
    isLoadingError: idvPreferenceLoadingError,
  } = useGetIdvPreference();
  const showChimeIdvMessage = useShowChimeIdvGuidance();

  const apiLoading = idvPreferenceLoading || getWizardEntitlementsLoading;
  const apiError =
    idvPreferenceError ||
    idvPreferenceLoadingError ||
    getWizardEntitlementsError;

  useEffect(() => {
    // Handle case where a component which was navigated to re-enable the next button, but then navigated back to the wizard chooser.
    // An example is clicking the Record Videos section, then clicking Back.
    if (state.wizardPath.current === undefined) {
      dispatch({
        type: ActionType.SET_HIDE_NEXT,
        hideNext: true,
        stepId: state.currentStep.id,
      });
    }
  }, [state.wizardPath.current]);

  // Run this block when the component is mounted.
  useEffect(() => {
    trackStructEvent({
      category: Day1EventCategory.IDVPrefSelect,
      action: Day1EventAction.SectionToggled,
    });
  }, []);

  /**
   * Set or un-set the chat support context, refPage, and refLink depending on the selected
   * card. Specifically, the *Contact Support* card is similar to a ChatSupportFlyoutLink,
   * so it should set the refPage and refLink for the Chat Widget to provide the correct
   * context to IT Support.
   */
  useEffect(() => {
    if (selectedCard === "ContactSupport") {
      dispatch({
        type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
        nonDirectedChatSupportContext: "ON_IVV_CONSENT_TO_BIOMETRICS",
      });
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
        chatSupportRefPage: "ezo-biometric-preference-contact-card",
      });
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_LINK,
        chatSupportRefLink: "ezo-inline",
      });
    } else {
      dispatch({
        type: ActionType.SET_NON_DIRECTED_CHAT_SUPPORT_CONTEXT,
        nonDirectedChatSupportContext: undefined,
      });
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_PAGE,
        chatSupportRefPage: "ezo-identity-check-select-preference",
      });
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_REF_LINK,
        chatSupportRefLink: "ezo-chat",
      });
    }
  }, [selectedCard]);

  // Set wizard path based on API result immediately, then reevaluate after all entitlements are loaded just in case.
  useEffect(() => {
    // Only run this if there is both a response from API **AND** if there has not any in flight.
    // If any are in flight, dealing with that should be taken care of elsewhere, otherwise this will cause a race condition and break the UI flow.
    const wizardPathPreviouslySelected =
      state.wizardPath.current || state.wizardPath.previous;

    if (
      // Ensure this is the first time a wizard path is being set to prevent looping logic every time the user visits this page.
      !wizardPathPreviouslySelected &&
      // Ensure there's actually data
      idvPreferenceFromApi &&
      // Ensure nothing has been submitted beforehand.
      !metadata?.isAnyInFlight
    ) {
      // Only record a metric that a pre-selected preference was used if one was actually delivered (not undefined or "NONE").
      if (idvPreferenceFromApi !== "NONE") {
        trackStructEvent({
          category: Day1EventCategory.IDVPrefSelect,
          action:
            idvPreferenceFromApi === "MANUAL"
              ? Day1EventAction.IDVAutoSelectManualUsed
              : Day1EventAction.IDVAutoSelectAutomatedUsed,
        });
      }

      const wizardPathType =
        IDV_PREFERENCE_TO_WIZARD_PATH_TYPE[idvPreferenceFromApi];

      // Current wizard path is only set when type is IVV since in the case of a manual PD1 fail where IDV preference was set to Manual or if there was no preference from API,
      // user should still be able to select Automated IVV from the selection card (which as seen below is only displayed when !state.wizardPath.current).
      if (wizardPathType === "IVV") {
        dispatch({
          type: ActionType.SET_WIZARD_PATH,
          wizardPath: wizardPathType,
        });
      }
    }
  }, [idvPreferenceFromApi, metadata]);

  /**
   * Allows the user to proceed without the selection required error as if the user is only eligible for biometrics,
   * no selection is required and the "selectedCard" should be set to "IVV"
   */
  useEffect(() => {
    entitlements.map((option) => {
      if (option.isOnlyAutoIvvEligible) {
        setSelectedCard("IVV");
      }
    });
  }, [entitlements]);

  /**
   * Keeps track of what the user clicked here while preventing re-render. This prevents rerender because the state of the checked item
   * is not communicated via changing item props.
   * @param evt Event data.
   */
  const handleCardClick = (evt: CardClickedCallbackEventBody) => {
    if (evt.cardType) {
      trackStructEvent({
        category: Day1EventCategory.IDVPrefSelect,
        action: CARD_TYPE_TO_SNOWPLOW_ACTION[evt.cardType],
      });
    }

    setSelectedCard(evt.cardType);
    if (selectionRequiredError) {
      setSelectionRequiredError(false);
    }
  };

  const handleSubmit = () => {
    if (!selectedCard) {
      // Show an error that this requires a selection.
      setSelectionRequiredError(true);
    } else if (selectedCard === "ContactSupport") {
      trackStructEvent({
        category: Day1EventCategory.IDVPrefSelect,
        action: Day1EventAction.IDVSelectContactSupport,
      });
      /**
       * New hire wants to onboard with IT Support over Chime. Show the chat flyout on submit.
       */
      dispatch({
        type: ActionType.SET_CHAT_SUPPORT_FLYOUT_VISIBLE,
        chatSupportFlyoutVisible: true,
      });
    } else {
      trackStructEvent({
        category: Day1EventCategory.IDVPrefSelect,
        action: Day1EventAction.ClickSubmit,
      });
      // Set the selection in the state, causing the selected wizard path to be shown.
      dispatch({
        type: ActionType.SET_WIZARD_PATH,
        wizardPath: selectedCard,
      });
    }
  };

  return (
    <div data-test-id={DATA_TEST_ID_WIZARD_PATH_CHOOSER}>
      <Spacer height="S200" />
      {apiLoading && (
        <Row justifyContent="center" alignItems="center">
          <Spinner size={SpinnerSize.Medium} />
        </Row>
      )}
      {!apiLoading && (
        <>
          {!metadata?.isOnlyAutoIvvEligible && (
            <>
              <Row>
                <Text dataTestId="WPCTitle">
                  <FormattedMessage id="identityCheck-pathchooser-title" />
                </Text>
              </Row>
              <Spacer height="S500" />
            </>
          )}
          {metadata?.isAnyInFlight && (
            <>
              <Row width="100%">
                <MessageBanner
                  type={MessageBannerType.Informational}
                  dataTestId="WPCInFlight"
                >
                  <FormattedMessage id="identityCheck-ivv-inFlight" />
                </MessageBanner>
              </Row>
              <Spacer height="S500" />
            </>
          )}
          <Row
            gridGap="S200"
            justifyContent="space-between"
            alignItems="stretch"
          >
            {/* No preference was made in the API, show everything available to the user. Else the wizard path will be set immediately on the API call. In the event of get preference error, display all entitlements anyway.
             * API will return whatever is in the column, but this only should care about the valid values of MANUAL, AUTOMATED. Everything else like undefined, empty string, random string, should be ignored and show
             * all options instead.
             */}
            {(!state.wizardPath.current || apiError) &&
              entitlements.map((option, index) => {
                return (
                  <IdentityVerificationSelectionCard
                    key={`${String(option.cardType)}-${index}`}
                    bodyi18nId={option.bodyi18nId}
                    bodyIcon={option.bodyIcon}
                    cardClickedCallback={
                      option.isOnlyAutoIvvEligible ? undefined : handleCardClick
                    }
                    dataTestId={option.dataTestId}
                    estimatedTimeToCompletionMinutes={
                      option.estimatedTimeToCompletionMinutes
                    }
                    height={option.height}
                    recommended={option.recommended}
                    titlei18nId={option.titlei18nId}
                    cardType={option.cardType}
                    verificationTypei18n={option.verificationTypei18n}
                    verificationTypeIcon={option.verificationTypeIcon}
                    width={option.isOnlyAutoIvvEligible ? "100%" : option.width}
                    selected={selectedCard === option.cardType}
                    disabled={option.disabled}
                    isOnlyAutoIvvEligible={option.isOnlyAutoIvvEligible}
                    idsRequiredi18n={getTextForIdsRequiredField(
                      state.requiredDocsWithGesturesCount,
                      option.cardType
                    )}
                  />
                );
              })}
          </Row>
          {showChimeIdvMessage && (
            <>
              <Spacer height="S200" />
              <ITChatVerificationGuidance
                eventCategory={Day1EventCategory.IDVPrefSelect}
              />
            </>
          )}
          <Spacer height="S500" />
          <Row
            justifyContent={selectionRequiredError ? "space-between" : "right"}
          >
            {selectionRequiredError && (
              <>
                <Row width="100%">
                  <MessageBanner
                    type={MessageBannerType.Error}
                    dataTestId="WPCSelectionRequired"
                  >
                    {metadata?.isAnyInFlight ? (
                      <FormattedMessage id="identityCheck-pathchooser-card-errorAnyInFlight" />
                    ) : (
                      <FormattedMessage id="identityCheck-pathchooser-card-errorSelectionRequired" />
                    )}
                  </MessageBanner>
                </Row>
              </>
            )}
            <Button
              onClick={handleSubmit}
              variant={ButtonVariant.Primary}
              dataTestId="WPCSubmitBtn"
            >
              <FormattedMessage
                id={
                  metadata?.isOnlyAutoIvvEligible
                    ? "identityCheck-pathchooser-continueBtn"
                    : "identityCheck-pathchooser-submitBtn"
                }
              />
            </Button>
          </Row>
        </>
      )}
    </div>
  );
};
